import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import LoadingSpinner from '../../../components/common/LoadingSpinner';
import { getOpsProgramsEx } from '../../../requests/opsRegistrationsExtra';
import {
  Card,
  inputClass,
  PageBody,
  PageHeader,
  Pagination,
  Pill,
  rowClickClass,
  SortTh,
  tableClass,
  tdClass,
  theadClass,
  thClass,
  Toolbar,
  useListSort,
} from '../ui';
import { GAP_LABELS, money, STATUSES } from './shared';

/**
 * The list's search, status filter, page and sort. Held by the page above
 * the list so they survive opening a program and coming back.
 */
export function useProgramListState() {
  const [search, setSearch] = useState('');
  const [q, setQ] = useState('');
  const [status, setStatus] = useState('Utilization');
  const [page, setPage] = useState(1);
  const { sortKey, sortDir, toggleSort } = useListSort();

  useEffect(() => {
    const t = setTimeout(() => {
      setQ(search);
      setPage(1);
    }, 400);
    return () => clearTimeout(t);
  }, [search]);

  return {
    search,
    setSearch,
    q,
    status,
    setStatus,
    page,
    setPage,
    sortKey,
    sortDir,
    toggleSort,
  };
}

export type TProgramListState = ReturnType<typeof useProgramListState>;

/** All programs: search, filter, sort, page; a row opens the program. */
const ProgramList: React.FC<{ state: TProgramListState }> = ({ state }) => {
  const {
    search,
    setSearch,
    q,
    status,
    setStatus,
    page,
    setPage,
    sortKey,
    sortDir,
    toggleSort,
  } = state;
  const [searchParams, setSearchParams] = useSearchParams();
  const gapsOnly = searchParams.get('gaps') === '1';
  const urlAccountId = searchParams.get('accountId') ?? '';
  const urlActive = searchParams.get('active') === '1';

  const { data, isLoading } = useQuery({
    queryKey: [
      'opsPrograms',
      q,
      status,
      gapsOnly,
      sortKey,
      sortDir,
      page,
      urlAccountId,
      urlActive,
    ],
    queryFn: () =>
      getOpsProgramsEx({
        accountId: urlAccountId || undefined,
        active: urlActive || undefined,
        sort: sortKey || undefined,
        dir: sortKey ? sortDir : undefined,
        q: q || undefined,
        status: status || undefined,
        gaps: gapsOnly || undefined,
        page,
      }),
  });

  const pages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  return (
    <>
      <PageHeader
        title="Programs"
        meta="Team Pass contracts — balances, members, consumption"
      />
      <Toolbar>
        <input
          className={`${inputClass} w-64`}
          placeholder="Search programs…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        <select
          className={inputClass}
          value={status}
          onChange={(e) => {
            setStatus(e.target.value);
            setPage(1);
          }}
        >
          <option value="">All statuses</option>
          {STATUSES.map((s) => (
            <option key={s}>{s}</option>
          ))}
        </select>
        <label className="flex items-center gap-2 text-sm text-gray-four">
          <input
            type="checkbox"
            className="ops-check"
            checked={gapsOnly}
            onChange={(e) => {
              setSearchParams(e.target.checked ? { gaps: '1' } : {});
              setPage(1);
            }}
          />
          Needs review — Program Information gaps
        </label>
      </Toolbar>

      {isLoading ? (
        <div className="flex items-center justify-center py-20">
          <LoadingSpinner />
        </div>
      ) : (
        <PageBody wide>
          <Card>
            <div className="overflow-x-auto">
              <table className={`${tableClass} min-w-[900px]`}>
                <thead className={theadClass}>
                  <tr>
                    <SortTh
                      k="number"
                      label="Number"
                      sort={sortKey}
                      dir={sortDir}
                      onSort={toggleSort}
                    />
                    <SortTh
                      k="name"
                      label="Program"
                      sort={sortKey}
                      dir={sortDir}
                      onSort={toggleSort}
                    />
                    <SortTh
                      k="account"
                      label="Account"
                      sort={sortKey}
                      dir={sortDir}
                      onSort={toggleSort}
                    />
                    <th className={thClass}>Status</th>
                    <SortTh
                      k="window"
                      label="Window"
                      sort={sortKey}
                      dir={sortDir}
                      onSort={toggleSort}
                    />
                    <SortTh
                      k="invested"
                      label="Invested"
                      sort={sortKey}
                      dir={sortDir}
                      onSort={toggleSort}
                      right
                    />
                    <SortTh
                      k="remaining"
                      label="Remaining"
                      sort={sortKey}
                      dir={sortDir}
                      onSort={toggleSort}
                      right
                    />
                    <SortTh
                      k="members"
                      label="Members"
                      sort={sortKey}
                      dir={sortDir}
                      onSort={toggleSort}
                      right
                    />
                    <th className={`${thClass} text-right`}>Gaps</th>
                  </tr>
                </thead>
                <tbody>
                  {data?.items.map((p) => (
                    <tr
                      key={p.id}
                      className={rowClickClass}
                      onClick={() => setSearchParams({ id: p.id })}
                    >
                      <td
                        className={`${tdClass} whitespace-nowrap font-mono text-xs text-gray-bold`}
                      >
                        {p.number ?? '—'}
                      </td>
                      <td className={`${tdClass} font-medium text-gray-four`}>
                        {p.name}
                      </td>
                      <td className={tdClass}>{p.accountName ?? '—'}</td>
                      <td className={tdClass}>
                        <Pill value={p.status} />
                      </td>
                      <td className={`${tdClass} whitespace-nowrap`}>
                        {p.startDate} → {p.endDate}
                      </td>
                      <td className={`${tdClass} whitespace-nowrap text-right`}>
                        {money(p.invested)}
                      </td>
                      <td className={`${tdClass} whitespace-nowrap text-right`}>
                        {money(p.remaining)}
                      </td>
                      <td className={`${tdClass} text-right`}>
                        {p.memberCount ?? '—'}
                      </td>
                      <td
                        className={`${tdClass} text-right`}
                        title={
                          p.gaps.missing.length
                            ? `Still to fill: ${p.gaps.missing
                                .map((k) => GAP_LABELS[k] ?? k)
                                .join(', ')}`
                            : 'Program Information complete'
                        }
                      >
                        {p.gaps.missing.length ? (
                          <Pill
                            value={String(p.gaps.missing.length)}
                            tone="warn"
                          />
                        ) : (
                          <span className="text-xs text-gray-bold">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Pagination
              page={page}
              pages={pages}
              total={data?.total}
              onPage={setPage}
            />
          </Card>
        </PageBody>
      )}
    </>
  );
};

export default ProgramList;
